import { Trans, useLingui } from '@lingui/react/macro';
import { Button, toast } from '@utima/ui';
import { Phone, PhoneOff } from 'lucide-react';
import { memo, useEffect, useRef, useState } from 'react';

import { useVoice } from './hooks/useVoice';

export const VoiceButton = memo(function VoiceButton({
  beforeConnect,
}: {
  beforeConnect?: () => Promise<unknown>;
}) {
  const { t } = useLingui();
  const { status, connect, disconnect, callDurationTimestamp } = useVoice();
  const isConnected = status.value === 'connected';
  const isConnecting = status.value === 'connecting';

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const [saving, setSaving] = useState(false);
  const handleClick = async () => {
    if (isConnected || isConnecting) {
      disconnect();
    } else {
      if (saving) return;
      setSaving(true);
      try {
        await beforeConnect?.();
        if (mounted.current) connect();
      } catch {
        toast.error(t`Save failed`, {
          description: t`Check the configuration before starting a call.`,
        });
      } finally {
        setSaving(false);
      }
    }
  };

  /**
   * Hang up if the user navigates away mid-call — on UNMOUNT ONLY.
   *
   * Read through refs with an empty dep array on purpose. Listing
   * `disconnect`/`status.value` as deps instead makes React run this
   * cleanup on every status change, not just unmount, and the cleanup then
   * closes over the PREVIOUS status — so the `connecting → connected`
   * transition would fire a `disconnect()` holding `'connecting'` and hang
   * up the call the instant it became ready.
   */
  const disconnectRef = useRef(disconnect);
  disconnectRef.current = disconnect;

  const statusRef = useRef(status.value);
  statusRef.current = status.value;

  useEffect(() => {
    return () => {
      if (statusRef.current !== 'disconnected') {
        disconnectRef.current();
      }
    };
  }, []);

  return (
    <Button
      type='button'
      data-cta='admin-voice-button'
      variant={isConnected ? 'danger' : 'success'}
      size='lg'
      onClick={handleClick}
      loading={isConnecting || saving}
      className={`shrink-0 ${isConnected ? 'animate-pulse' : ''}`}
      title={
        isConnected
          ? t`Stop recording`
          : isConnecting
            ? t`Connecting…`
            : t`Start recording`
      }
    >
      {isConnected ? (
        <>
          <PhoneOff className='size-5' />
          <Trans>Hang up</Trans> {callDurationTimestamp}
        </>
      ) : isConnecting ? (
        <>
          <Phone className='size-5' />
          <Trans>Connecting…</Trans>
        </>
      ) : (
        <>
          <Phone className='size-5' />
          {beforeConnect ? (
            <Trans>Save and call agent</Trans>
          ) : (
            <Trans>Call Agent</Trans>
          )}
        </>
      )}
    </Button>
  );
});
